// src/components/dashboard/AddRelativeModal.js
// Updated: 10/17/2025 02:02 PM

import React, { useState } from 'react';
import { db } from '../../firebase/config';
import { doc, setDoc, updateDoc, arrayUnion, writeBatch } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import Spinner from '../common/Spinner';
import ModalWrapper from '../common/ModalWrapper';

const AddRelativeModal = ({ familyId, anchorMember, relationshipType, onClose }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState(relationshipType === 'spouse' ? (anchorMember.gender === 'Male' ? 'Female' : 'Male') : '');
  const [loading, setLoading] = useState(false);

  const title = `Add ${anchorMember.firstName}'s ${relationshipType}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const newMemberId = uuidv4();
    const batch = writeBatch(db);

    // 1. Create the new person's document
    const newMemberRef = doc(db, 'users', newMemberId);
    const newMemberData = {
      firstName,
      lastName: lastName || anchorMember.lastName, // Default to same last name
      familyId,
      gender,
      dob: '', dod: '', isDeceased: false,
      email: null, profilePictureUrl: '',
    };

    // 2. Define the relationship link
    if (relationshipType === 'child') {
      if (anchorMember.gender === 'Male') newMemberData.fatherId = anchorMember.id;
      else newMemberData.motherId = anchorMember.id;
    }
    batch.set(newMemberRef, newMemberData);

    // 3. Update the anchor member and the family list
    const anchorMemberRef = doc(db, 'users', anchorMember.id);
    if (relationshipType === 'spouse') {
      batch.update(anchorMemberRef, { spouseId: newMemberId });
      batch.update(newMemberRef, { spouseId: anchorMember.id }); // Link back
    }

    const familyDocRef = doc(db, 'families', familyId);
    batch.update(familyDocRef, { members: arrayUnion(newMemberId) });

    try {
      await batch.commit();
      toast.success('Family member added and linked!');
      onClose();
    } catch (error) {
      console.error("Error adding relative:", error);
      toast.error("Failed to add relative.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose} size="md">
      <div className="p-6 border-b"><h3 className="text-xl font-semibold text-neutral-gray">{title}</h3></div>
      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name</label><input type="text" id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/></div>
            <div><label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name</label><input type="text" id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={anchorMember.lastName} className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/></div>
          </div>
          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700">Gender</label>
            <select id="gender" value={gender} onChange={(e) => setGender(e.target.value)} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm">
              <option value="">Select...</option><option value="Male">Male</option><option value="Female">Female</option>
            </select>
          </div>
        </div>
        <div className="p-6 bg-gray-50 rounded-b-lg flex justify-end items-center gap-4">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={loading} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50 flex items-center">{loading ? <Spinner size="sm" /> : 'Save and Link'}</button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default AddRelativeModal;